import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerHeader from "@/components/CustomerHeader";
import { Star, ShieldCheck, Coins, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const CARD_TYPES = new Set(["tarot", "katina"]);
const SPREAD_OPTIONS = [
  { key: "3-card", n: 3, label: "3 Kart" },
  { key: "5-card", n: 5, label: "5 Kart" },
  { key: "10-card", n: 10, label: "10 Kart" },
];

export default function Falcilar() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [question, setQuestion] = useState("");
  const [type, setType] = useState("kahve");
  const [spread, setSpread] = useState("3-card");
  const [pickedCards, setPickedCards] = useState([]);
  const [deck, setDeck] = useState({ tarot: [], katina: [] });
  const [busy, setBusy] = useState(false);
  const { user, refreshUser } = useAuth();

  useEffect(() => { api.get("/falcilar").then(({ data }) => setItems(data)); }, []);
  useEffect(() => { if (selected) api.get("/tarot-deck").then(({ data }) => setDeck(data)).catch(() => {}); }, [selected]);

  const isCardType = CARD_TYPES.has(type);
  const spreadN = SPREAD_OPTIONS.find(s => s.key === spread)?.n || 3;
  const cardsSource = type === "tarot" ? deck.tarot : type === "katina" ? deck.katina : [];

  const toggleCard = (idx) => {
    setPickedCards(prev => prev.includes(idx) ? prev.filter(x => x !== idx) : prev.length < spreadN ? [...prev, idx] : prev);
  };
  const shuffle = () => {
    const pool = [...Array(cardsSource.length).keys()];
    for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
    setPickedCards(pool.slice(0, spreadN));
  };
  const resetPicks = () => setPickedCards([]);

  const openBooking = (f) => {
    setSelected(f); setQuestion(""); setPickedCards([]);
    const firstSpec = (f.specialties || [])[0] || "Kahve";
    setType(firstSpec.toLowerCase());
    setSpread("3-card");
  };

  const book = async () => {
    if (!selected) return;
    if (isCardType && pickedCards.length !== spreadN) {
      toast.error(`Lütfen tam olarak ${spreadN} kart seç`); return;
    }
    setBusy(true);
    try {
      const payload = { falci_id: selected.id, fortune_type: type, question };
      if (isCardType) { payload.tarot_spread = spread; payload.tarot_cards = pickedCards; }
      const { data } = await api.post("/consultations", payload);
      toast.success(data.message || "Falcıya bildirildi");
      await refreshUser();
      setSelected(null); setQuestion(""); setPickedCards([]);
      window.location.href = `/sohbet/${data.id}`;
    } catch (e) { toast.error(e.response?.data?.detail || "Rezervasyon başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <CustomerHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif-myst text-4xl md:text-5xl mb-2">Gerçek Falcılar</h1>
        <p className="text-white/60 mb-8">AI yerine, deneyimli bir uzmandan kişisel yorum al.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="falcilar-grid">
          {items.map((f) => (
            <div key={f.id} data-testid={`falci-${f.id}`} className="glass rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="relative">
                <img src={f.avatar_url} alt={f.name} className="w-full aspect-[4/3] object-cover" />
                {f.is_online && <div className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"><span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Çevrimiçi</div>}
                {f.is_verified && <div className="absolute top-3 right-3 gold-text"><ShieldCheck className="w-5 h-5" /></div>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-serif-myst text-2xl">{f.name}</div>
                    <div className="text-xs text-white/50">{f.title}</div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 gold-text"><Star className="w-4 h-4 fill-[#D4AF37]" />{f.rating}</div>
                    <div className="text-[10px] text-white/40">({f.reviews_count})</div>
                  </div>
                </div>
                <p className="text-sm text-white/60 mt-2 line-clamp-2">{f.bio}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(f.specialties || []).map((s) => (
                    <span key={s} className="bg-white/5 text-white/70 text-[10px] px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="token-pill px-2.5 py-1 rounded-full text-xs flex items-center gap-1"><Coins className="w-3 h-3" /> {f.price_per_reading}</div>
                  <button onClick={() => openBooking(f)} data-testid={`book-${f.id}`} className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" /> Fal Al
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="col-span-full text-center text-white/50 py-16">Henüz falcı eklenmedi. Admin panelden ekleyin.</div>}
        </div>

        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
            <div className="glass-strong rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <img src={selected.avatar_url} alt={selected.name} className="w-14 h-14 rounded-full object-cover gold-border" />
                <div>
                  <div className="font-serif-myst text-2xl">{selected.name}</div>
                  <div className="text-xs text-white/50">{selected.price_per_reading} jeton / yorum</div>
                </div>
              </div>

              <label className="text-xs text-white/60 mb-1 block mt-4">Fal türü</label>
              <select value={type} onChange={(e) => { setType(e.target.value); setPickedCards([]); }} data-testid="book-type-select" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white">
                {(selected.specialties || []).map((s) => <option key={s} value={s.toLowerCase()}>{s}</option>)}
              </select>

              {isCardType && (
                <>
                  <label className="text-xs text-white/60 mb-1 block mt-3">Kart açılımı</label>
                  <div className="flex gap-2 mb-3">
                    {SPREAD_OPTIONS.map(s => (
                      <button key={s.key} type="button" onClick={() => { setSpread(s.key); setPickedCards([]); }} data-testid={`book-spread-${s.key}`}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${spread === s.key ? "gold-border gold-text bg-[#D4AF37]/10" : "border-white/10 text-white/70"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-white/60">Kartlarını seç <span className="gold-text">({pickedCards.length}/{spreadN})</span></div>
                    <div className="flex gap-2">
                      <button type="button" onClick={shuffle} data-testid="book-shuffle" className="btn-ghost text-xs px-3 py-1 rounded-full">🎲 Karıştır</button>
                      <button type="button" onClick={resetPicks} data-testid="book-reset-cards" className="btn-ghost text-xs px-3 py-1 rounded-full">Temizle</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5 max-h-52 overflow-y-auto p-2 bg-black/20 rounded-xl" data-testid="book-card-grid">
                    {cardsSource.map((c, i) => {
                      const picked = pickedCards.includes(i);
                      const order = pickedCards.indexOf(i) + 1;
                      return (
                        <button type="button" key={i} onClick={() => toggleCard(i)} data-testid={`book-card-${i}`}
                          className={`aspect-[2/3] rounded-md relative transition-all ${picked ? "gold-border ring-2 ring-[#D4AF37] scale-105" : "bg-gradient-to-br from-[#2b1c4a] to-[#150e2b] hover:from-[#3a2666]"}`}
                          title={c.name || c.n}>
                          {picked ? (
                            <span className="absolute inset-0 flex items-center justify-center gold-text font-bold text-lg">{order}</span>
                          ) : (
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white/30">?</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <label className="text-xs text-white/60 mb-1 block mt-3">Sorun / Notun</label>
              <textarea value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="book-question" rows={3} placeholder="Falcıya iletmek istediğin bilgi..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white" />

              <div className="flex gap-2 mt-4">
                <button onClick={book} disabled={busy || (user && user.tokens < selected.price_per_reading) || (isCardType && pickedCards.length !== spreadN)} data-testid="confirm-book" className="btn-gold flex-1 py-3 rounded-full flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Onayla (${selected.price_per_reading} jeton)`}
                </button>
                <button onClick={() => setSelected(null)} className="btn-ghost px-6 py-3 rounded-full">İptal</button>
              </div>
              {user && user.tokens < selected.price_per_reading && <div className="text-xs text-red-300 mt-2">Jetonun yetersiz — mağazadan al</div>}
              {isCardType && pickedCards.length !== spreadN && <div className="text-[11px] text-white/40 mt-2 text-center">Onaylamak için {spreadN} kart seçmelisin.</div>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
